import * as React from 'react';
import { useSync } from '@/contexts/SyncContext';
import { cn } from '@/lib/utils';

const PULL_THRESHOLD_PX = 72;
const MAX_PULL_PX = 96;

interface PullToRefreshProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Mobile pull-to-refresh that calls SyncContext.forceRefresh when released past threshold.
 * Disabled on md+ viewports so desktop scroll is unchanged.
 */
export function PullToRefresh({ children, className }: PullToRefreshProps) {
  const { forceRefresh, isOnline, status } = useSync();
  const [pullDistance, setPullDistance] = React.useState(0);
  const [refreshing, setRefreshing] = React.useState(false);
  const startYRef = React.useRef<number | null>(null);
  const pullingRef = React.useRef(false);

  const canPull = !refreshing && isOnline && status !== 'syncing';

  const onTouchStart = (event: React.TouchEvent) => {
    if (!canPull) return;
    if (window.scrollY > 0) {
      startYRef.current = null;
      return;
    }
    startYRef.current = event.touches[0]?.clientY ?? null;
    pullingRef.current = false;
  };

  const onTouchMove = (event: React.TouchEvent) => {
    if (startYRef.current == null || !canPull) return;
    if (window.scrollY > 0) {
      startYRef.current = null;
      setPullDistance(0);
      return;
    }
    const currentY = event.touches[0]?.clientY ?? startYRef.current;
    const delta = currentY - startYRef.current;
    if (delta <= 0) {
      setPullDistance(0);
      pullingRef.current = false;
      return;
    }
    pullingRef.current = true;
    // Resist further pull past max for a native feel
    const resisted = Math.min(MAX_PULL_PX, delta * 0.45);
    setPullDistance(resisted);
  };

  const onTouchEnd = async () => {
    if (!pullingRef.current) {
      startYRef.current = null;
      setPullDistance(0);
      return;
    }
    const shouldRefresh = pullDistance >= PULL_THRESHOLD_PX && canPull;
    startYRef.current = null;
    pullingRef.current = false;

    if (!shouldRefresh) {
      setPullDistance(0);
      return;
    }

    setRefreshing(true);
    setPullDistance(40);
    try {
      await forceRefresh();
    } finally {
      setRefreshing(false);
      setPullDistance(0);
    }
  };

  const showHint = pullDistance > 8 || refreshing;

  return (
    <div
      className={cn('relative flex flex-1 flex-col md:contents', className)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      <div
        className={cn(
          'pointer-events-none absolute left-0 right-0 top-0 z-20 flex justify-center md:hidden',
          'transition-[height,opacity] duration-150 ease-out'
        )}
        style={{
          height: showHint ? Math.max(pullDistance, refreshing ? 40 : 0) : 0,
          opacity: showHint ? 1 : 0,
        }}
        aria-hidden={!showHint}
      >
        <div className="mt-2 flex items-center gap-2 rounded-full bg-background/95 px-3 py-1.5 text-xs text-muted-foreground shadow-sm ring-1 ring-border">
          <span
            className={cn(
              'inline-block h-3.5 w-3.5 rounded-full border-2 border-muted-foreground/40 border-t-primary',
              (refreshing || pullDistance >= PULL_THRESHOLD_PX) && 'animate-spin'
            )}
          />
          <span>
            {refreshing
              ? 'Refreshing…'
              : pullDistance >= PULL_THRESHOLD_PX
                ? 'Release to refresh'
                : 'Pull to refresh'}
          </span>
        </div>
      </div>
      <div
        className="flex flex-1 flex-col md:transform-none"
        style={
          pullDistance > 0 || refreshing
            ? { transform: `translateY(${Math.max(pullDistance, refreshing ? 40 : 0)}px)` }
            : undefined
        }
      >
        {children}
      </div>
    </div>
  );
}
